import { useState, type ReactNode } from "react";
import { Button, ConfirmDialog, Modal, SectionHeader, XIcon } from "@fawkes/ui";

export interface AssignmentItem<Id extends string | number> {
  id: Id;
  label: string;
  /**
   * Non-null when this item cannot be acted on. The row is rendered but inert, with the explanation
   * beside it — a silently missing row would leave the admin wondering where a branch went.
   */
  blockedReason?: string | null;
}

export interface AssignmentChipLabels {
  loading: string;
  loadFailed: string;
  /** Shown in place of the chips when nothing is assigned yet. */
  empty: string;
  add: string;
  addDialogTitle: string;
  addDialogEmpty: string;
  removeTitle: string;
  /** Body of the remove confirmation, given the item's label. */
  removeBody: (label: string) => string;
  removeConfirm: string;
  cancel: string;
  /** Accessible name for a chip's ✕ button, given the item's label. */
  removeAria: (label: string) => string;
  /** Placeholder + accessible name for the add dialog's filter box. */
  searchPlaceholder: string;
  /** Shown when the filter matches nothing. */
  searchNoMatches: string;
}

export interface AssignmentChipSectionProps<Id extends string | number> {
  title: string;
  help?: string;
  /** What the user currently holds. `null` means "not known" — still loading, or the read failed. */
  assigned: AssignmentItem<Id>[] | null;
  /** What can still be added. Already-held items must be excluded by the caller/server. */
  options: AssignmentItem<Id>[] | null;
  isError?: boolean;
  /** Disables every control while a write is in flight, so two clicks cannot race. */
  busy?: boolean;
  /** Rendered above the chips — e.g. a "this user reaches every branch anyway" note. */
  notice?: ReactNode;
  /** Rendered INSTEAD of the whole editor — e.g. "this account has no legacy link". */
  unavailable?: ReactNode;
  actionError?: string | null;
  /** Turns a blockedReason into a human explanation. Omit when items are never blocked. */
  describeBlocked?: (item: AssignmentItem<Id>) => string;
  labels: AssignmentChipLabels;
  onAdd: (id: Id) => void;
  onRemove: (id: Id) => void;
  className?: string;
}

/**
 * A set of assignments shown as removable chips, with an "add" dialog listing only what is not held
 * yet — the shape shared by the admin user screen's "Rollen" and "Vestigingen" sections.
 *
 * <p>Each add and remove is its own write, so there is no save button and nothing to forget to press.
 * That also removes the hazard the previous checkbox-and-save-the-whole-set design carried: a write is
 * now a single explicit delta, never a full set reconstructed from whatever the screen happened to have
 * loaded. The one invariant kept from that design is the important one — until the current assignment
 * is known, no chips and no add button are offered at all.
 *
 * <p>Removals are confirmed, because a chip's ✕ is a small target for an action that changes what
 * someone can reach.
 */
export function AssignmentChipSection<Id extends string | number>({
  title,
  help,
  assigned,
  options,
  isError = false,
  busy = false,
  notice,
  unavailable,
  actionError = null,
  describeBlocked,
  labels,
  onAdd,
  onRemove,
  className,
}: AssignmentChipSectionProps<Id>) {
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState("");
  const [pendingRemoval, setPendingRemoval] = useState<AssignmentItem<Id> | null>(null);

  if (unavailable) {
    return (
      <section className={className}>
        <SectionHeader>{title}</SectionHeader>
        {unavailable}
      </section>
    );
  }

  // Same rule as the design this replaces: never offer controls over an unknown read.
  if (assigned === null) {
    return (
      <section className={className}>
        <SectionHeader>{title}</SectionHeader>
        {isError ? (
          <p className="fawkes-error" role="alert">
            {labels.loadFailed}
          </p>
        ) : (
          <p className="fawkes-muted">{labels.loading}</p>
        )}
      </section>
    );
  }

  function confirmRemove() {
    if (pendingRemoval) {
      onRemove(pendingRemoval.id);
      setPendingRemoval(null);
    }
  }

  return (
    <section className={className}>
      <SectionHeader>{title}</SectionHeader>
      {help && <p className="fawkes-muted">{help}</p>}
      {notice}

      {assigned.length === 0 ? (
        <p className="fawkes-muted">{labels.empty}</p>
      ) : (
        <ul className="assignment-chips">
          {assigned.map((item) => {
            const blocked = item.blockedReason ? describeBlocked?.(item) : undefined;
            return (
              <li
                key={item.id}
                className={`assignment-chip${blocked ? " assignment-chip--blocked" : ""}`}
                aria-disabled={blocked ? true : undefined}
              >
                <span className="assignment-chip__label">{item.label}</span>
                <button
                  type="button"
                  className="assignment-chip__remove"
                  aria-label={labels.removeAria(item.label)}
                  disabled={busy || Boolean(blocked)}
                  onClick={() => setPendingRemoval(item)}
                >
                  <XIcon width={12} height={12} />
                </button>
                {/* The reason is rendered as visible text, not a title: a disabled button is out of the
                    tab order and swallows mouse events, so a tooltip on it would reach nobody. */}
                {blocked && <span className="assignment__reason">{blocked}</span>}
              </li>
            );
          })}
        </ul>
      )}

      {actionError && (
        <p className="fawkes-error" role="alert">
          {actionError}
        </p>
      )}

      <Button
        variant="secondary"
        onClick={() => {
          setQuery("");
          setAdding(true);
        }}
        disabled={busy || options === null}
      >
        {labels.add}
      </Button>

      {adding && options !== null && (() => {
        const needle = query.trim().toLowerCase();
        const shown = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
        return (
        <Modal open title={labels.addDialogTitle} onClose={() => setAdding(false)} footer={
          <Button variant="secondary" onClick={() => setAdding(false)}>
            {labels.cancel}
          </Button>
        }>
          {options.length === 0 ? (
            <p className="fawkes-muted">{labels.addDialogEmpty}</p>
          ) : (
            <>
            {/* Only worth the chrome once the list is long enough to scan badly. */}
            {options.length > 8 && (
              <div className="fawkes-products-search assignment-options__search">
                <input
                  type="search"
                  autoComplete="off"
                  className="fawkes-products-search__input"
                  placeholder={labels.searchPlaceholder}
                  aria-label={labels.searchPlaceholder}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            )}
            {shown.length === 0 ? (
              <p className="fawkes-muted">{labels.searchNoMatches}</p>
            ) : (
            // Listbox semantics to match the app's other branch picker (VestigingSwitcher), which a
            // screen reader user may well meet first.
            <ul className="assignment-options" role="listbox" aria-label={labels.addDialogTitle}>
              {shown.map((item) => {
                const blocked = item.blockedReason ? describeBlocked?.(item) : undefined;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={false}
                      aria-disabled={blocked ? true : undefined}
                      className="assignment-options__item"
                      disabled={busy || Boolean(blocked)}
                      onClick={() => {
                        onAdd(item.id);
                        setAdding(false);
                      }}
                    >
                      <span>{item.label}</span>
                      {blocked && <span className="assignment__reason">{blocked}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
            )}
            </>
          )}
        </Modal>
        );
      })()}

      {pendingRemoval && (
        <ConfirmDialog
          title={labels.removeTitle}
          confirmLabel={labels.removeConfirm}
          cancelLabel={labels.cancel}
          danger
          busy={busy}
          onConfirm={confirmRemove}
          onCancel={() => setPendingRemoval(null)}
        >
          {labels.removeBody(pendingRemoval.label)}
        </ConfirmDialog>
      )}
    </section>
  );
}
